/**
 * DscrEntryTabs — Full Entry ↔ Quick Entry, the two ways into the DSCR sizer.
 *
 * These were two Admin → Sizers tabs, which read as two tools. They are not:
 * both post the SAME endpoint (`POST /pricing/scenario/dscr_quick`), render the
 * same `DscrResultsColumn`, and persist to the same scenario store. The only
 * difference is HOW MANY QUESTIONS get asked — a per-unit rent grid and a
 * vacancy basis versus a single rent override. That is a mode of one screen, so
 * it is a switcher on that screen rather than a second place to go.
 *
 * THE MODE LIVES IN THE URL (`?entry=quick`) rather than in state, so a chosen
 * mode survives a refresh, is linkable, and answers the browser Back button. It
 * is a QUERY param and not a path segment on purpose: the admin sub-tab strip
 * derives its highlight from `pathname.startsWith(subTab.to)`, so a second path
 * would have to be a second nav entry — exactly what this replaces.
 *
 * Level 1 (teal) per CLAUDE.md's active-state rule: the switcher changes WHERE
 * YOU ARE — a different screen, a different URL — not which slice of data the
 * current screen shows. The recipe comes from `selectedStateSx` via
 * `ToggleTabSwitcher`; nothing is hand-rolled here.
 */
import { useSearchParams } from "react-router-dom";

import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import { Box } from "@mui/material";

export const DSCR_ENTRY_TABS = ["Full Entry", "Quick Entry"];

/** `?entry=` → which screen renders. Anything but `quick` is the full form. */
export const dscrEntryMode = (searchParams) =>
  searchParams?.get("entry") === "quick" ? "quick" : "full";

export default function DscrEntryTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const quick = dscrEntryMode(searchParams) === "quick";

  return (
    <Box sx={{ borderBottom: "1px solid #E0E0E0", px: 0.5 }}>
      <ToggleTabSwitcher
        level={1}
        variant="underline"
        sx={{ mb: 0 }}
        size="large"
        fontSize={"16px"}
        tabs={DSCR_ENTRY_TABS}
        value={quick ? "Quick Entry" : "Full Entry"}
        onChange={(label) => {
          const next = new URLSearchParams(searchParams);
          // `full` is the default, so it is expressed by the param's ABSENCE —
          // the canonical URL for the sizer stays `/sizers/dscr`.
          if (label === "Quick Entry") next.set("entry", "quick");
          else next.delete("entry");
          // A resumed scenario belongs to the full form: quick entry asks a subset
          // of its questions and has no resume path, so switching modes drops the
          // id rather than carrying a scenario the other form cannot load.
          next.delete("scenario");
          setSearchParams(next);
        }}
      />
    </Box>
  );
}
